/** Page-view counting for the admin Traffic tab (our own server, no third-party analytics, no cookies).
 *  Sends only: the page path, a random visitor id this browser made up, where the visitor came from (the other
 *  site's address, reduced to its domain on the server) and the login token if signed in. Browsers with
 *  "Do Not Track" switched on are not counted. */
import { MARKET_API_BASE, getAuthToken } from "./backendConfig";

const VISITOR_KEY = "lwt.visitor";

/** Convert SPA routes into stable page keys; the terminal tab hash is a page too. */
export function pageViewPath(pathname, hash = "") {
  if (pathname === "/app") {
    const section = String(hash || "").replace(/^#/, "").toLowerCase();
    return ["chart", "python", "backtest"].includes(section) ? `/app/${section}` : "/app/chart";
  }
  if (pathname === "/python") return "/app/python";
  if (pathname === "/backtest") return "/app/backtest";
  return pathname || "/";
}


/** Random id for this browser, made once and kept. Pure apart from storage. */
export function visitorId() {
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id || !/^[A-Za-z0-9_-]{8,40}$/.test(id)) {
      const bytes = new Uint8Array(12);
      window.crypto.getRandomValues(bytes);
      id = "v" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function doNotTrack() {
  return navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.globalPrivacyControl === true;
}

let lastPath = "";
let lastAt = 0;
let firstView = true;

/** Count one page view. Never throws and never slows the page down. */
export function trackPageView(path) {
  try {
    if (doNotTrack()) return;
    const now = Date.now();
    if (path === lastPath && now - lastAt < 2000) return; // React re-renders, double effects
    lastPath = path;
    lastAt = now;
    const visitor = visitorId();
    if (!visitor) return;
    // the outside referrer only matters for the first page; later pages are clicks inside the site
    const referrer = firstView ? document.referrer || "" : "";
    firstView = false;
    const token = getAuthToken();
    fetch(`${MARKET_API_BASE}/track`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ path, visitor, referrer }),
    }).catch(() => {});
  } catch {
    // tracking must never break the page
  }
}

/** Count an approved landing-page button click using the same anonymous browser id. */
export function trackLandingAction(action) {
  try {
    if (doNotTrack()) return;
    const visitor = visitorId();
    if (!visitor) return;
    const token = getAuthToken();
    fetch(`${MARKET_API_BASE}/track`, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ path: "/", visitor, action, referrer: document.referrer || "" }),
    }).catch(() => {});
  } catch {
    // tracking must never break the page
  }
}
